import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, Dialog, Field, Input, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../api/manufacturing";
import { KanbanClient, type KanbanLineDTO } from "../../api/production";
import { formatCover, formatQuantity, ZoneBadge } from "../purchasing/replenishment/shared";

/**
 * The kanban pipeline: every part made on runs of its own and taken from stock
 * (a stocking point replenished by MAKE), planned like a bought material on
 * its usage there: the ones needing a run first. "Put into production" makes
 * a planned kanban order, its BOM generated, to confirm and release as usual.
 */
export function KanbanPipeline({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new KanbanClient(http), [http]);
  const plan = useQuery({ queryKey: ["production", "kanban", "plan"], queryFn: () => client.plan() });
  const [starting, setStarting] = useState<KanbanLineDTO | null>(null);

  const cols = useMemo(() => {
    const c = defineColumns<KanbanLineDTO>();
    return [
      c.custom(
        "part_number",
        (row) => row.part_number,
        (row) => (
          <span className="truncate">
            <Link
              to="/manufacturing/parts/$partId"
              params={{ partId: String(row.part_id) }}
              className="font-medium text-accent underline-offset-2 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.part_number}
            </Link>
            <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
          </span>
        ),
        { headerKey: "column.part.number", priority: 1 },
      ),
      c.text("warehouse_code", (row) => row.warehouse_code, { header: t("stocking.column.warehouse", "Warehouse"), filter: "select", priority: 3 }),
      c.custom("zone", (row) => row.zone, (row) => <ZoneBadge zone={row.zone} />, { header: t("kanban.column.zone", "Zone"), filter: "select", priority: 1 }),
      c.custom(
        "days_of_cover",
        (row) => (row.days_of_cover == null ? Number.MAX_SAFE_INTEGER : Number(row.days_of_cover)),
        (row) => <span className="tabular-nums">{formatCover(row.days_of_cover, t)}</span>,
        { header: t("kanban.column.cover", "Cover"), priority: 2 },
      ),
      c.custom(
        "average_daily_usage",
        (row) => Number(row.average_daily_usage),
        (row) => <span className="tabular-nums">{formatQuantity(row.average_daily_usage)}</span>,
        { header: t("kanban.column.usage", "Used a day"), priority: 3 },
      ),
      c.number("on_hand", (row) => Number(row.on_hand), { header: t("kanban.column.on-hand", "On hand"), priority: 2 }),
      c.number("in_production", (row) => Number(row.in_production), { header: t("kanban.column.in-production", "In production"), priority: 2 }),
      c.number("reserved", (row) => Number(row.reserved), { header: t("kanban.column.reserved", "Needed by orders"), priority: 3 }),
      c.custom(
        "suggested",
        (row) => Number(row.suggested),
        (row) => <span className={Number(row.suggested) > 0 ? "font-semibold tabular-nums" : "tabular-nums text-ink-faint"}>{formatQuantity(row.suggested)}</span>,
        { header: t("kanban.column.suggested", "Run to start"), priority: 1 },
      ),
      c.number("open_runs", (row) => row.open_runs, { header: t("kanban.column.open-runs", "Open runs"), priority: 3 }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", defaultVisible: false }),
    ];
  }, [t]);

  return (
    <>
      <DataTable<KanbanLineDTO>
        data={plan.data ?? []}
        columns={cols}
        getRowId={(row) => `${String(row.part_id)}-${String(row.warehouse_id)}`}
        loading={plan.isLoading}
        stateKey="/production/pipeline/kanban"
        rowActions={(row) =>
          canWrite ? (
            <Button size="sm" variant={Number(row.suggested) > 0 ? "secondary" : "ghost"} onClick={() => setStarting(row)}>
              {t("kanban.start", "Put into production")}
            </Button>
          ) : null
        }
        emptyMessage={t("kanban.empty", "No kanban part is planned: give a part a stocking point replenished by Made.")}
      />
      {starting && <StartRunDialog line={starting} onClose={() => setStarting(null)} />}
    </>
  );
}

function StartRunDialog({ line, onClose }: { line: KanbanLineDTO; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const client = useMemo(() => new KanbanClient(http), [http]);
  const [quantity, setQuantity] = useState(String(Number(line.suggested) > 0 ? Number(line.suggested) : (line.production_multiple ?? "")));
  const valid = Number(quantity) > 0;
  const start = useMutation({
    mutationFn: () => client.run(line.part_id, line.warehouse_id, Number(quantity)),
    onSuccess: async (order) => {
      await queryClient.invalidateQueries({ queryKey: ["production"] });
      onClose();
      toast.success(t("kanban.started", "Run {{number}} created, to confirm and release", { number: order.number }));
      void navigate({ to: "/production/orders/$orderId", params: { orderId: String(order.id) } });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("kanban.start.title", "Put {{part}} into production", { part: line.part_number })}
      description={t("kanban.start.description", "A planned kanban run is created with its production BOM; confirm and release it as any other order.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={start.isPending} onClick={() => start.mutate()}>
            {t("kanban.start.confirm", "Create the run")}
          </Button>
        </>
      }
    >
      <Field
        label={t("kanban.start.quantity", "Quantity")}
        htmlFor="kanban-quantity"
        hint={line.production_multiple ? t("kanban.start.multiple", "Made in multiples of {{n}}.", { n: line.production_multiple }) : undefined}
      >
        <Input id="kanban-quantity" type="number" min="1" step="1" value={quantity} invalid={!valid} onChange={(event) => setQuantity(event.target.value)} />
      </Field>
    </Dialog>
  );
}
